import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, LinkedinIcon } from 'lucide-react';
interface OurTeamProps {
  onContactOpen: () => void;
}
interface TeamMember {
  id: string;
  name: string;
  title: string;
  role: string;
  image: string;
  bio: string;
  linkedin?: string;
}
const teamMembers: TeamMember[] = [{
  id: 'dj',
  name: 'DJ Panfili',
  title: 'Founder & CEO',
  role: 'Management',
  image: 'https://cdn.magicpatterns.com/uploads/o8LfULY8SjUCiYkiUGjHZD/DJ_Headshot_5.png',
  bio: "DJ has spent his career studying one thing: what makes a business owner say yes. As a growth strategist who has helped companies scale past eight figures and generated over $35 million in revenue, he understands the psychology, the motivations, and the fears of founders at every stage of the journey. At Deal Flow, DJ leads the firm's go-to-market strategy and client acquisition — ensuring that every seller who walks through our process is matched with the right buyers, the right structure, and the right outcome. He built this firm because he believed founders deserved a fundamentally better exit experience than what the market was offering.",
  linkedin: 'https://www.linkedin.com/in/gaeton-panfili-041937271/'
}, {
  id: 'ciaran',
  name: 'Ciaran Houlihan',
  title: 'COO & Co-Founder',
  role: 'Management',
  image: 'https://cdn.magicpatterns.com/uploads/1DfaFXLotCKSWjjthbHQxw/Aragon-Headshot-Ciaran-Houlihan-2026-02-26-19.jpg',
  bio: "Ciaran has spent over a decade building the systems that drive results at scale — and at Deal Flow, he applies that same precision to the sell-side process. As COO, Ciaran oversees the operational infrastructure that powers our competitive auction model: the outreach systems, the buyer pipeline, and the process architecture that ensures every engagement runs with institutional-grade efficiency. He has worked alongside dozens of founders navigating high-stakes transitions, and he understands that a great exit isn't just about the number — it's about the experience of getting there. Ciaran ensures ours is one you'll want to talk about.",
  linkedin: 'https://www.linkedin.com/in/ciaranhoulihan/'
}, {
  id: 'devesh',
  name: 'Devesh Sharma',
  title: 'Managing Director',
  role: 'Management',
  image: 'https://cdn.magicpatterns.com/uploads/twsLpeFX4wQvVeLXZ1AZzv/Devesh-pic_v2.jpg',
  bio: 'Devesh is a veteran operator and M&A strategist who brings the rigor of a multi-billion dollar conglomerate to the lower middle market. Before joining Deal Flow, he spent 15 years at Bosch, where he served as CFO of $1B+ divisions and Head of M&A for North and South America, personally overseeing 40–50 acquisitions per year. With a decade of private equity experience and a track record of over 100 closed transactions, Devesh specializes in identifying and unlocking hidden value before a sale begins. His signature approach was recently proven when he took a company with a $90M initial offer and orchestrated a $240M exit just one year later. At Deal Flow, Devesh leverages his deep operational background in manufacturing, construction, and business services to ensure our clients never leave money on the table.',
  linkedin: 'https://www.linkedin.com/in/deveshsharma1/'
}, {
  id: 'khai',
  name: 'Khai Yong',
  title: 'Head of AI',
  role: 'Technology',
  image: 'https://cdn.magicpatterns.com/uploads/qq8pqsQYTNEoAe2yD1RovW/Khai_Headshot.jpg',
  bio: "Khai leads Deal Flow's AI and data infrastructure — the engine that powers our ability to identify, qualify, and engage the right buyers for every business we represent. With a background advising venture-backed companies and a track record recognized by both Facebook and Google as a case study in performance, Khai brings a level of analytical precision to buyer targeting that no traditional M&A firm can match. His work ensures that when we go to market with your business, we're not casting a wide net — we're placing your opportunity directly in front of the buyers most likely to pay a premium for it.",
  linkedin: 'https://www.linkedin.com/in/khaiyong/'
}, {
  id: 'nick',
  name: 'Nick Eubanks',
  title: 'Advisor',
  role: 'Advisory Board',
  image: 'https://cdn.magicpatterns.com/uploads/vhQou8RoRpqrs5RLfmoJhs/thumb_Nick_Headshot.jpg',
  bio: "Nick is a serial entrepreneur and SEO pioneer who has driven 100 billion visits and $100 billion in online revenue for global brands. He founded agency From The Future and co-created Traffic Think Tank, which he sold to Semrush for ~$80 million in 2023. Nick's playbooks for content flywheels, organic demand capture, and data-layer monetization inform our long-term moat: turning every seller interaction into proprietary insight. Nick guides brand authority, inbound deal flow, and strategy.",
  linkedin: 'https://www.linkedin.com/in/nickeubanks/'
}, {
  id: 'elliot',
  name: 'Elliot Martinez',
  title: 'Strategic Partner',
  role: 'Advisory Board',
  image: 'https://cdn.magicpatterns.com/uploads/sF1aLoJJcq5sFPYAF84wTE/thumb_Elliiot_Headshot.jpg',
  bio: "Elliot is a deal-maker who has executed $250M in private-sector transactions spanning real estate, healthcare, and lower-middle-market roll-ups. Formerly a commercial real-estate principal, he now advises PE operators on capital structuring, lender relations, and post-close value creation. Elliot's superpower is assembling the right people around the right opportunity — accelerating diligence, funding, and operational lift-off. His people-over-profits ethos keeps culture and returns aligned, making him an invaluable compass as we scale from agency to tech-enabled investment platform.",
  linkedin: 'https://www.linkedin.com/in/elliot-martinez-4b93711b7/'
}];
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function OurTeam({
  onContactOpen
}: OurTeamProps) {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const managementTeam = teamMembers.filter(m => m.role === 'Management');
  const technologyTeam = teamMembers.filter(m => m.role === 'Technology');
  const advisoryBoard = teamMembers.filter(m => m.role === 'Advisory Board');
  const renderGrid = (members: TeamMember[], bg: string) => <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06]" data-id="element-579">
      {members.map((member, index) => <motion.div key={member.id} {...fadeInUp} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} onClick={() => setSelectedMember(member)} className={`group cursor-pointer ${bg}`} data-id="element-580">
          <div className="relative overflow-hidden" data-id="element-581">
            <img src={member.image} alt={member.name} className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0" data-id="element-582" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" data-id="element-583" />
            <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-584">
              <h3 className="text-lg font-bold text-white" data-id="element-585">{member.name}</h3>
              <p className="text-white/50 text-xs mt-0.5" data-id="element-586">{member.title}</p>
            </div>
          </div>
          <div className="p-6 border-t border-white/[0.06]" data-id="element-587">
            <span className="text-deal-green text-xs font-medium tracking-wide group-hover:underline" data-id="element-588">
              Read Bio →
            </span>
          </div>
        </motion.div>)}
    </div>;
  return <main className="bg-deal-black pt-20" data-id="element-589">
      {/* Page Header */}
      <section className="py-20 md:py-28 border-b border-white/[0.06]" data-id="element-590">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-591">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-592">
            <span className="inline-block text-deal-green text-[11px] font-semibold tracking-[0.25em] uppercase mb-6 border border-deal-green/30 px-4 py-1.5" data-id="element-593">
              Our Team
            </span>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight" data-id="element-594">
            The People Behind
            <br data-id="element-595" />
            <span className="text-deal-green" data-id="element-596">Your Exit.</span>
          </motion.h1>
        </div>
      </section>

      {/* Management Team */}
      <section className="py-20 md:py-28 bg-[#0d0d0d]" data-id="element-597">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-598">
          <motion.div {...fadeInUp} className="mb-12" data-id="element-599">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold" data-id="element-600">
              Management Team
            </p>
            <div className="w-8 h-px bg-deal-green/40 mt-3" data-id="element-601" />
          </motion.div>
          {renderGrid(managementTeam, 'bg-[#0d0d0d]')}
        </div>
      </section>

      {/* Technology */}
      <section className="py-20 md:py-28 bg-deal-black" data-id="element-602">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-603">
          <motion.div {...fadeInUp} className="mb-12" data-id="element-604">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold" data-id="element-605">
              Technology
            </p>
            <div className="w-8 h-px bg-deal-green/40 mt-3" data-id="element-606" />
          </motion.div>
          {renderGrid(technologyTeam, 'bg-deal-black')}
        </div>
      </section>

      {/* Advisory Board */}
      <section className="py-20 md:py-28 bg-deal-black" data-id="element-607">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-608">
          <motion.div {...fadeInUp} className="mb-12" data-id="element-609">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold" data-id="element-610">
              Advisory Board
            </p>
            <div className="w-8 h-px bg-deal-green/40 mt-3" data-id="element-611" />
          </motion.div>
          {renderGrid(advisoryBoard, 'bg-deal-black')}
        </div>
      </section>

      {/* Bio Modal */}
      <AnimatePresence data-id="element-612">
        {selectedMember && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={() => setSelectedMember(null)} data-id="element-613">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} transition={{
          duration: 0.3,
          type: 'spring',
          damping: 25
        }} className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#111111] border border-white/10 shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-614">
              <button onClick={() => setSelectedMember(null)} className="absolute top-6 right-6 p-2 text-white/30 hover:text-white transition-colors z-10" aria-label="Close modal" data-id="element-615">
                <XIcon className="w-5 h-5" data-id="element-616" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-3" data-id="element-617">
                <div className="md:col-span-1" data-id="element-618">
                  <img src={selectedMember.image} alt={selectedMember.name} className="w-full h-64 md:h-full object-cover grayscale" data-id="element-619" />
                </div>
                <div className="md:col-span-2 p-8 md:p-10" data-id="element-620">
                  <span className="text-deal-green text-[10px] font-semibold tracking-[0.2em] uppercase" data-id="element-621">
                    {selectedMember.title}
                  </span>
                  <h2 className="text-2xl font-bold text-white mt-2 mb-1" data-id="element-622">
                    {selectedMember.name}
                  </h2>
                  <div className="w-8 h-px bg-deal-green mt-4 mb-6" data-id="element-623" />
                  <p className="text-white/55 leading-relaxed text-sm mb-8" data-id="element-624">
                    {selectedMember.bio}
                  </p>
                  {selectedMember.linkedin && <a href={selectedMember.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-deal-green hover:text-deal-green-dark transition-colors text-sm" data-id="element-625">
                      <LinkedinIcon className="w-4 h-4" data-id="element-626" />
                      <span data-id="element-627">Connect on LinkedIn</span>
                    </a>}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d] relative overflow-hidden" data-id="element-628">
        <div className="absolute inset-0 bg-gradient-to-t from-deal-green/[0.04] to-transparent" data-id="element-629" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center" data-id="element-630">
          <motion.div {...fadeInUp} data-id="element-631">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-632">
              Get Started
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight" data-id="element-633">
              Meet the Team In Person
            </h2>
            <div className="w-12 h-px bg-deal-green/40 mx-auto mb-8" data-id="element-634" />
            <p className="text-lg text-white/45 mb-12 max-w-2xl mx-auto font-light" data-id="element-635">
              Schedule a confidential consultation and let us show you how our
              unique team can deliver the exit you deserve.
            </p>
            <button onClick={onContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300" data-id="element-636">
              Schedule a Consultation
            </button>
          </motion.div>
        </div>
      </section>
    </main>;
}